import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../pages/CustomButton";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import Loader from "../../dist/Loader";
import EmployeeMapTracker from "../../pages/EmployeeMapTracker";
import XLSX from "xlsx-js-style";
export default function EmployeeWorkingReport() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [mapModal, setMapModal] = useState({
    isOpen: false,
    employeeData: null,
  });

  const [selectedState, setSelectedState] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedMonthYear, setSelectedMonthYear] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dataFetched, setDataFetched] = useState(false);

  const tableContainerRef = useRef(null);
  const employeeRef = useRef(null);

  const [showEmployeeRequired, setShowEmployeeRequired] = useState(false);

  const currentYear = new Date().getFullYear();
  const monthYearOptions = [];

  for (let i = 11; i >= 0; i--) {
    const date = new Date(currentYear, i, 1);
    const fullMonthName = date.toLocaleString("default", { month: "long" });
    const shortMonthName = date.toLocaleString("default", { month: "short" });
    const apiValue = `${fullMonthName} ${currentYear}`;
    const displayLabel = `${shortMonthName} ${currentYear}`;

    monthYearOptions.push({
      value: apiValue,
      label: displayLabel,
    });
  }
  const openCompareModal = ({ employeeId, employeeName, trackDate }) => {
    setMapModal({
      isOpen: true,
      employeeData: {
        employeeId,
        employeeName,
        trackDate,
        context: "compare", // 👈 important for SR/JR logic
      },
    });
  };

  const closeMapModal = () => {
    setMapModal({
      isOpen: false,
      employeeData: null,
    });
  };

  useEffect(() => {
    const currentDate = new Date();
    const currentFullMonth = currentDate.toLocaleString("default", {
      month: "long",
    });
    const currentYear = currentDate.getFullYear();
    setSelectedMonthYear(`${currentFullMonth} ${currentYear}`);

    fetchStates();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchEmployees();
    } else {
      setEmployees([]);
      setSelectedEmployee("");
    }
    setShowEmployeeRequired(false);
  }, [selectedState]);

  const fetchStates = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setStates(data);
    } catch (err) {
      console.error("Error fetching states:", err);
      setError("Failed to fetch states");
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/employees?state_id=${selectedState}`,
        { headers: getAuthHeaders() },
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Failed to fetch employees");
    }
  };

  const handleGo = async () => {
    if (!selectedMonthYear) {
      alert("Please select Month/Year");
      return;
    }

    const hasState = selectedState && selectedState !== "all";
    const hasEmployee = selectedEmployee && selectedEmployee !== "all";

    if (hasState && !hasEmployee) {
      setShowEmployeeRequired(true);
      if (employeeRef.current) {
        employeeRef.current.focus();
      }
      return;
    }

    if (!hasState && !hasEmployee) {
      alert("Please select State or Employee before clicking Go");
      return;
    }

    setShowEmployeeRequired(false);

    setLoading(true);
    setError("");
    setDataFetched(false);

    try {
      const params = new URLSearchParams({ monthYear: selectedMonthYear });

      if (hasState) params.append("state_id", String(selectedState));
      if (hasEmployee) params.append("employee_id", String(selectedEmployee));

      const url = `${BASE_URL}/api/reports/actual-working?${params.toString()}`;
      console.log("API URL:", url);

      const response = await fetch(url, { headers: getAuthHeaders() });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail ||
          errorData?.message ||
          `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();
      const mappedData = (data || []).map((item, index) => {
        const workingDate =
          item.working_date ||
          item.work_date ||
          item.workingDate ||
          item.workDate ||
          item.date ||
          "";

        const dateOnly =
          item.date ||
          item.work_date ||
          item.working_date ||
          item.workingDate ||
          item.workDate ||
          "";

        const empCode =
          item.emp_code ||
          item.employee_code ||
          item.empCode ||
          item.code ||
          item.emp_code_str ||
          item.emp_code_id ||
          "";

        const employeeId =
          item.employee_id ||
          item.emp_id ||
          item.id ||
          item.user_id ||
          item.empId ||
          item.employeeId ||
          "";

        const maybeEmpFromName =
          typeof item.employee === "string" && item.employee.trim()
            ? item.employee.trim()
            : "";

        const finalEmpCode =
          empCode || (hasEmployee ? String(selectedEmployee) : "");
        const finalEmployeeId =
          employeeId || (hasEmployee ? String(selectedEmployee) : "");

        return {
          sno: item.s_no || item.sno || item.index || index + 1,
          date: dateOnly,
          workingDate,
          leave: item.leave_meeting || item.leave || "",
          employee:
            item.employee ||
            item.emp_name ||
            item.name ||
            maybeEmpFromName ||
            "",
          employeeId: finalEmployeeId,
          empCode: finalEmpCode,
          workingWithEmployeeId: Array.isArray(item.working_with_employee_id)
            ? item.working_with_employee_id
            : item.working_with_employee_id
              ? [item.working_with_employee_id]
              : [],
          empTpWw: item.emp_tp_ww || item.tp_ww || "",
          empTpRoute:
            item.emp_tp_route_city || item.tp_route || item.route_city || "",
          empRouteCovered: item.emp_route_covered || item.route_covered || "",
          empTpRetailer: item.emp_tp_retailer || item.tp_retailer || "",
          empCalls: item.emp_calls || item.calls || "",
          empWwCalls: item.emp_ww_calls || item.ww_calls || "",
          empStockist: item.emp_stockist || item.stockist || "",
          workingWith: item.working_with || item.ww || "",
          wwDesig: item.ww_desig || item.designation || "",
          wwTotalCalls: item.ww_total_calls || item.total_calls || "",
          wwEmpTotalCalls:
            item.ww_emp_total_calls || item.emp_total_calls || "",
          wwProductiveCalls: item.ww_prod_calls || item.productive_calls || "",
          wwPob: item.ww_pob || item.pob || "",
          locationMatch:
            item.location_match !== undefined && item.location_match !== null
              ? Number(item.location_match)
              : "-",

          _raw: item,
        };
      });

      setTableData(mappedData);
      setDataFetched(true);
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError(err.message || "Failed to fetch report data");
      setDataFetched(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeClick = (empCodeOrId, workingDate, employeeName) => {
    if (!empCodeOrId) {
      alert(`Cannot navigate: Missing employee identifier for ${employeeName}`);
      return;
    }
    if (!workingDate) {
      alert(`Cannot navigate: Missing working date for ${employeeName}`);
      return;
    }
    const formattedDate = encodeURIComponent(String(workingDate));
    const encodedEmp = encodeURIComponent(String(empCodeOrId));
    window.open(
      `/reporting/employee-working?employeeId=${encodedEmp}&working_date=${formattedDate}`,
      "_blank",
      "width=1400,height=700",
    );
  };
  const toYMD = (d) => {
    const date = new Date(d);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const handleCallCountClick = (
    employeeIdOrCode,
    date,
    callCount,
    workingWithEmployeeId
  ) => {
    if (!employeeIdOrCode || !date) return;

    const formattedDate = toYMD(date);
    const encodedId = encodeURIComponent(employeeIdOrCode);

    let url = `/admin/dcrpreviewtable?employeeId=${encodedId}&date=${formattedDate}`;

    const wwId = Array.isArray(workingWithEmployeeId)
      ? workingWithEmployeeId[0]
      : workingWithEmployeeId;

    if (
      wwId !== undefined &&
      wwId !== null &&
      wwId !== "" &&
      wwId !== "undefined"
    ) {
      url += `&manager_ids=${encodeURIComponent(wwId)}`;
    }

    window.open(url, "_blank", "width=800,height=600");
  };

  const columns = [
    { key: "sno", label: "S.No", width: "60px" },
    { key: "date", label: "Date", width: "100px" },
    { key: "leave", label: "Leave Meeting", width: "100px" },
    {
      key: "employee",
      label: "Employee",
      width: "150px",
      isLink: true,
      handler: "employee",
    },
    { key: "empTpWw", label: "Emp TP WW", width: "120px" },
    { key: "empTpRoute", label: "Emp TP Route/City", width: "150px" },
    { key: "empRouteCovered", label: "Emp Route Covered", width: "150px" },
    { key: "empTpRetailer", label: "Emp TP Retailer", width: "120px" },
    {
      key: "empCalls",
      label: "Emp Calls",
      width: "80px",
      isLink: true,
      handler: "calls",
    },
    { key: "empWwCalls", label: "Emp WW Calls", width: "100px" },
    { key: "empStockist", label: "Emp Stockist", width: "100px" },
    { key: "workingWith", label: "Working With", width: "120px" },
    { key: "wwDesig", label: "WW Desig", width: "80px" },
    {
      key: "wwTotalCalls",
      label: "WW Total Calls",
      width: "100px",
      isLink: true,
      handler: "calls",
    },
    { key: "wwEmpTotalCalls", label: "WW Emp Total Calls", width: "120px" },
    { key: "wwProductiveCalls", label: "WW Prod Calls", width: "120px" },
    { key: "wwPob", label: "WW POB", width: "80px" },
    {
      key: "compare",
      label: "Compare",
      width: "90px",
      isCompare: true,
    },
    { key: "locationMatch", label: "Location Match", width: "100px" },
  ];

  const gridTemplate = columns
    .map((col) => {
      if (
        col.key === "employee" ||
        col.key === "empTpRoute" ||
        col.key === "workingWith"
      ) {
        return "minmax(150px, 2fr)";
      }
      if (col.key === "date" || col.key === "leave") {
        return "minmax(100px, 1fr)";
      }
      if (
        col.key.includes("calls") ||
        col.key.includes("pob") ||
        col.key === "sno"
      ) {
        return "minmax(70px, 0.7fr)";
      }
      return "minmax(90px, 1fr)";
    })
    .join(" ");
  const displayRows = dataFetched
    ? tableData.length > 0
      ? tableData
      : []
    : [];

  const formatEmployeeName = (employee) => {
    if (!employee) return "";
    if (typeof employee === "object") {
      const firstName = employee.first_name || "";
      const lastName = employee.last_name || "";
      const name = `${firstName} ${lastName}`.trim();
      return name || employee.name || "";
    }
    return String(employee);
  };

  const getSelectedEmployeeName = () => {
    if (!selectedEmployee) return "All Employees";
    const emp = employees.find(
      (e) => String(e.id) === String(selectedEmployee),
    );
    return emp ? formatEmployeeName(emp) : "Employee";
  };

  const getSelectedStateName = () => {
    if (!selectedState) return "All States";
    const state = states.find((s) => String(s.id) === String(selectedState));
    return state ? state.name : "State";
  };

  const getDisplayMonthYear = () => {
    if (!selectedMonthYear) return "Month Year";
    const [fullMonth, year] = selectedMonthYear.split(" ");
    const date = new Date(`${fullMonth} 1, ${year}`);
    const shortMonth = date.toLocaleString("default", { month: "short" });
    return `${shortMonth} ${year}`;
  };

  // Print function: builds a full HTML table from columns + tableData and prints it
  const handlePrint = async () => {
    try {
      setLoading(true);

      if (!tableData || tableData.length === 0) {
        alert("Nothing to print");
        setLoading(false); // <-- ensure spinner cleared
        return;
      }

      const escapeHtml = (unsafe) => {
        if (unsafe === null || unsafe === undefined) return "";
        return String(unsafe)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };

      // Build header cells with full labels
      const headerCells = columns
        .map((c) => `<th>${escapeHtml(c.label)}</th>`)
        .join("");

      // Build body rows in column order
      const bodyRows = tableData
        .map((row) => {
          const cells = columns
            .map((c) => {
              let raw = row[c.key];

              // For object values, pick sensible display values
              if (typeof raw === "object" && raw !== null) {
                const name =
                  raw.name ||
                  raw.first_name ||
                  raw.firstName ||
                  raw.fullName ||
                  raw.full_name;
                if (name) {
                  return `<td>${escapeHtml(name)}</td>`;
                }
                try {
                  raw = JSON.stringify(raw);
                } catch {
                  raw = String(raw);
                }
              }

              // If the UI shows a link (employee or calls), use displayed text where possible
              // fallback to raw value
              return `<td>${escapeHtml(raw ?? "")}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      // Compose printable HTML with styles designed to show full headings and full cell content
      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Print - Actual Working</title>
            <style>
              html,body { margin:0; padding:0; font-family: Arial, Helvetica, sans-serif; color: #222; }
              .container { padding: 12px; }
              .report-title { margin-bottom: 12px; font-size: 14px; font-weight: 700; }
              table { border-collapse: collapse; width: 100%; font-size: 12px; table-layout: auto; }
              thead th {
                background: #e6f7fb;
                border: 1px solid #c6c6c6;
                padding: 8px;
                text-align: left;
                vertical-align: middle;
                white-space: normal;
                word-break: break-word;
              }
              tbody td {
                border: 1px solid #e0e0e0;
                padding: 6px;
                vertical-align: top;
                white-space: normal;
                word-break: break-word;
              }

              /* Try to mimic your column widths while allowing wrap for printing */
              ${columns
          .map((c, i) => {
            const raw = c.width || "120px";
            // Apply min-width so columns don't collapse but allow wrap
            return `th:nth-child(${i + 1}), td:nth-child(${i + 1
              }) { min-width: ${raw}; }`;
          })
          .join("\n")}

              @media print {
                thead { display: table-header-group; }
                tr { page-break-inside: avoid; }
                @page { size: auto; margin: 12mm; }
                body { -webkit-print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="report-title">Actual Working - ${escapeHtml(
            getSelectedEmployeeName(),
          )} (${escapeHtml(getSelectedStateName())} | ${escapeHtml(
            getDisplayMonthYear(),
          )})</div>

              <table>
                <thead>
                  <tr>${headerCells}</tr>
                </thead>
                <tbody>
                  ${bodyRows}
                </tbody>
              </table>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open("", "_blank", "width=1200,height=800");
      if (!printWindow) {
        alert(
          "Unable to open print window. Please allow popups for this site.",
        );
        setLoading(false);
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      // ensure content settles
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (err) {
          console.error("Print failed:", err);
        } finally {
          setLoading(false);
        }
      }, 300);
    } catch (err) {
      console.error("Print error:", err);
      alert("Failed to print: " + (err.message || err));
      setLoading(false);
    }
  };

  // Export as CSV which opens in Excel
  const handleExportExcel = () => {
  if (!tableData.length) {
    alert("No data");
    return;
  }

  const exportData = tableData.map((row) => {
    const obj = {};
    columns.forEach((col) => {
      obj[col.label] = row[col.key] ?? "";
    });
    return obj;
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  const range = XLSX.utils.decode_range(ws["!ref"]);

  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
    fill: { fgColor: { rgb: "2563EB" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: {
      top: { style: "thin", color: { rgb: "FF000000" } },
      bottom: { style: "thin", color: { rgb: "FF000000" } },
      left: { style: "thin", color: { rgb: "FF000000" } },
      right: { style: "thin", color: { rgb: "FF000000" } },
    },
  };

  const rowStyle = {
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "FF000000" } },
      bottom: { style: "thin", color: { rgb: "FF000000" } },
      left: { style: "thin", color: { rgb: "FF000000" } },
      right: { style: "thin", color: { rgb: "FF000000" } },
    },
  };

  const setDateCell = (r, c, rawVal, s) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (rawVal) {
      try {
        const date = new Date(
          /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
            ? `${rawVal}T00:00:00`
            : rawVal
        );
        if (!isNaN(date.getTime())) {
          ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
          return;
        }
      } catch {}
    }
    ws[addr] = { v: "", t: "s", s };
  };

  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });

      if (!ws[addr]) ws[addr] = { t: "z", v: "" };

      ws[addr].s = R === 0 ? headerStyle : rowStyle;
    }
  }

  const dateKeyPattern = /(date|doj|dor|^dob$|resign|joining)/i;

  columns.forEach((col, colIndex) => {
    if (!dateKeyPattern.test(col.key) && !dateKeyPattern.test(col.label)) return;

    tableData.forEach((row, index) => {
      setDateCell(index + 1, colIndex, row[col.key], rowStyle);
    });
  });

  ws["!cols"] = columns.map(() => ({ wch: 18 }));

  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  ws["!autofilter"] = {
    ref: `A1:${XLSX.utils.encode_col(range.e.c)}1`,
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Report");

  XLSX.writeFile(wb, "Report.xlsx");
};
  return (
    <div className="w-full min-h-screen bg-gray-50 p-4">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <Loader onCancel={() => setLoading(false)} />
        </div>
      )}
      <div className="max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-300 mb-6 relative overflow-visible">
          <div className="border-b border-gray-300 bg-gray-50 px-4 py-2">
            {/* FIX: use className not class */}
            <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
              <span className="text-blue-700">Actual </span>
              <span className="text-blue-500">Working</span>
            </h1>
          </div>

          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <div className="flex items-center">
                <label className="text-sm font-bold text-gray-800 w-32">
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={(e) => {
                    setSelectedState(e.target.value);
                    setSelectedEmployee("");
                    setDataFetched(false);
                    setShowEmployeeRequired(false);
                  }}
                  className="flex-1 px-3 py-1.5 border border-gray-400 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select state</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-start flex-col md:flex-row md:items-center">
                <label className="text-sm font-bold text-gray-800 w-32">
                  Employee
                </label>
                <div className="flex-1">
                  <select
                    ref={employeeRef}
                    value={selectedEmployee}
                    onChange={(e) => {
                      setSelectedEmployee(e.target.value);
                      setDataFetched(false);
                      setShowEmployeeRequired(false);
                    }}
                    className={`w-full px-3 py-1.5 border rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${showEmployeeRequired
                      ? "border-red-500"
                      : "border-gray-400"
                      }`}
                    disabled={!selectedState}
                    aria-invalid={showEmployeeRequired ? "true" : "false"}
                    aria-describedby={
                      showEmployeeRequired ? "employee-required-msg" : undefined
                    }
                  >
                    <option value="">Select employee</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {`${employee.first_name ?? ""} ${employee.last_name ?? ""} ${employee.employee ?? ""} -
    (${employee.designation ?? ""} | ${employee.headquarter_name ?? ""} | ${employee.division ?? ""} | ${employee.state_name ?? ""})`}
                      </option>
                    ))}
                  </select>

                  {showEmployeeRequired && (
                    <div
                      id="employee-required-msg"
                      className="text-xs text-red-600 mt-1"
                    >
                      Please select an employee for the selected state.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 items-end">
              <div className="flex items-center">
                <label className="text-sm font-bold text-gray-800 w-32">
                  Month/Year
                </label>
                <select
                  value={selectedMonthYear}
                  onChange={(e) => {
                    setSelectedMonthYear(e.target.value);
                    setDataFetched(false);
                  }}
                  className="flex-1 px-3 py-1.5 border border-gray-400 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {monthYearOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mr-4">
                <CustomButton
                  variant="go"
                  onClick={handleGo}
                  disabled={loading}
                  className="break-normal"
                >
                  {loading ? "Loading..." : "GO"}
                </CustomButton>
              </div>
            </div>

            {error && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                <strong>Error:</strong> {error}
              </div>
            )}
          </div>
        </div>

        {dataFetched && (
          <>
            <h2 className="mb-3 text-sm font-bold text-gray-800">
              Actual Working - {getSelectedEmployeeName()} (
              {getSelectedStateName()} | {getDisplayMonthYear()})
            </h2>

            {/* Single table container that scrolls entire table together */}
            <div
              className="border rounded shadow table-wrapper relative"
              ref={tableContainerRef}
            >
              {/* Table header - fixed at top */}
              <div className="sticky top-0 z-10">
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: gridTemplate,
                  }}
                >
                  {columns.map((col, cIdx) => (
                    <div
                      key={col.key}
                      className={`p-2 text-xs font-bold text-center text-gray-800 border-b border-r border-gray-400 bg-cyan-100 ${cIdx === 0 ? "sticky left-0 z-40" : ""
                        }`}
                    >
                      <div title={col.label}>{col.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Table body - scrolls with header */}
              <div className="flex flex-col">
                {displayRows.length > 0 ? (
                  displayRows.map((row, rIdx) => {
                    const isSunday = (row.leave || "")
                      .toLowerCase()
                      .includes("sunday");

                    return (
                      <div
                        key={rIdx}
                        className="grid"
                        style={{
                          gridTemplateColumns: gridTemplate,
                        }}
                      >
                        {columns.map((col, cIdx) => {
                          const value = row[col.key] ?? "";
                          const isFirst = cIdx === 0;

                          const empIdentifier =
                            row.employeeId ||
                            row.empCode ||
                            (row.employee ? String(row.employee) : "") ||
                            (selectedEmployee ? String(selectedEmployee) : "");

                          const linkDate = row.workingDate || row.date || "";

                          const isEmployeeLink =
                            col.handler === "employee" &&
                            value &&
                            empIdentifier &&
                            linkDate;

                          const callIdentifier =
                            row.employeeId ||
                            row.empCode ||
                            (row.employee ? String(row.employee) : "") ||
                            (selectedEmployee ? String(selectedEmployee) : "");

                          const isCallLink =
                            col.handler === "calls" &&
                            value &&
                            callIdentifier &&
                            linkDate &&
                            (col.key !== "wwTotalCalls" || row.workingWithEmployeeId?.length > 0);

                          const bg = isFirst
                            ? "bg-blue-100"
                            : isSunday
                              ? "bg-green-100"
                              : rIdx % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50";
                          if (col.isCompare) {
                            const hasLocationMatch =
                              Number(row.locationMatch) > 0; // ✅ only > 0 allowed

                            return (
                              <div
                                key={col.key}
                                className={`p-2 text-xs border-b border-r border-gray-300 ${bg} text-center`}
                              >
                                {empIdentifier &&
                                  linkDate &&
                                  hasLocationMatch ? (
                                  <span
                                    className="text-blue-600 underline cursor-pointer hover:text-blue-800"
                                    onClick={() =>
                                      openCompareModal({
                                        employeeId: empIdentifier,
                                        employeeName: row.employee,
                                        trackDate: linkDate,
                                      })
                                    }
                                  >
                                    Compare
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </div>
                            );
                          }

                          const textAlign =
                            col.key.includes("sno") ||
                              col.key.includes("calls") ||
                              col.key.includes("pob")
                              ? "text-center"
                              : "text-left";

                          return (
                            <div
                              key={col.key}
                              className={`p-2 text-xs border-b border-r border-gray-300 ${bg} ${textAlign} ${isFirst
                                ? "sticky left-0 z-20 font-semibold"
                                : ""
                                }`}
                            >
                              {isEmployeeLink ? (
                                <div
                                  className=" text-blue-600 underline cursor-pointer hover:text-blue-800"
                                  onClick={() =>
                                    handleEmployeeClick(
                                      empIdentifier,
                                      linkDate,
                                      value,
                                    )
                                  }
                                  title={`Click to view location history for ${value}`}
                                >
                                  {value}
                                </div>
                              ) : isCallLink ? (
                                <div
                                  className="text-blue-600 underline cursor-pointer hover:text-blue-800 text-center"
                                  onClick={() =>
                                    handleCallCountClick(
                                      col.key === "wwTotalCalls"
                                        ? row.workingWithEmployeeId?.[0]  // manager ID (1979)
                                        : callIdentifier,
                                      linkDate,
                                      value,
                                      col.key === "wwTotalCalls"
                                        ? String(selectedEmployee)         // ← directly use selectedEmployee, no fallback needed
                                        : undefined
                                    )
                                  }
                                  title={`Click to view DCR for ${value} calls`}
                                >
                                  {value}
                                </div>
                              ) : (
                                <div
                                  className={` ${textAlign === "text-center"
                                    ? "text-center"
                                    : ""
                                    }`}
                                  title={String(value)}
                                >
                                  {value}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No data available for the selected filters.
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-4 justify-center">
                <CustomButton
                  onClick={handlePrint}
                  label="Print"
                  variant="print"
                >
                  🖨
                </CustomButton>
                <CustomButton
                  onClick={handleExportExcel}
                  label="Export"
                  variant="export"
                >
                  📥
                </CustomButton>
              </div>
            </div>
          </>
        )}
        {/* ================= MAP COMPARE MODAL ================= */}
        {mapModal.isOpen && mapModal.employeeData && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-2">
            <div className="bg-white rounded-lg w-full max-w-7xl h-[95vh] flex flex-col shadow-xl">
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-10">
                <h3 className="text-xl font-bold text-gray-800">
                  Employee Location Tracker –{" "}
                  {mapModal.employeeData.employeeName}
                  <span className="text-sm text-gray-500 ml-2">
                    (Date: {mapModal.employeeData.trackDate})
                  </span>
                </h3>
                <button
                  onClick={closeMapModal}
                  className="text-gray-500 hover:text-gray-700 text-3xl font-bold bg-gray-100 hover:bg-gray-200 w-10 h-10 rounded-full flex items-center justify-center"
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 p-1 overflow-hidden">
                <EmployeeMapTracker
                  key={`${mapModal.employeeData.employeeId}-${mapModal.employeeData.trackDate}-${mapModal.employeeData.context}`}
                  employeeId={mapModal.employeeData.employeeId}
                  trackDate={mapModal.employeeData.trackDate}
                  employeeName={mapModal.employeeData.employeeName}
                  context={mapModal.employeeData.context}
                  isModal={true}
                  onClose={closeMapModal}
                />
              </div>
            </div>
          </div>
        )}
        {/* ===================================================== */}

        {!dataFetched && !loading && (
          <div className="text-center py-8 bg-white rounded border border-gray-300">
            <div className="text-gray-500 text-sm mb-2">
              Please select filters and click "Go" to load data.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
